import { useCallback } from 'react';
import { renameSession, deleteSession, listAttachments, uploadAttachment, deleteAttachment } from './api';
import { resetTransientTurnState } from '../chatTurnState';
import type { HomeChatSessionReconciliation } from './useHomeChatSessionReconciliation';
import type { HomeChatHydration } from './useHomeChatHydration';
import type { HomeChatPanelState } from './useHomeChatPanelState';
import type { HomeChatSessions } from './useHomeChatSessions';


type Inputs = Pick<HomeChatSessionReconciliation,
  | 'refreshSessions'
> &
Pick<HomeChatHydration,
  | 'notify'
> &
Pick<HomeChatPanelState,
  | 'setSessions'
  | 'activeSessionId'
  | 'activeSessionIdRef'
  | 'setActiveSessionId'
  | 'setGpcChatView'
  | 'setMrChatView'
  | 'setConstructionSowChatView'
  | 'setServicesPwsChatView'
  | 'setRehydratingGpcSessionId'
  | 'setMessages'
  | 'setAttachments'
  | 'setTurns'
  | 'setChatCards'
  | 'clearPendingIntentChoiceSelection'
  | 'setInFlight'
  | 'setPendingRequestIdSynced'
  | 'setInFlightUserMsg'
  | 'setPendingClarification'
  | 'setInFlightClarification'
  | 'setPendingTopicClarification'
  | 'setInFlightTopicFocus'
> &
Pick<HomeChatSessions,
  | 'ensureSession'
>;

export function useHomeChatSessionActions({
  refreshSessions,
  notify,
  setSessions,
  activeSessionId,
  activeSessionIdRef,
  setActiveSessionId,
  setGpcChatView,
  setMrChatView,
  setConstructionSowChatView,
  setServicesPwsChatView,
  setRehydratingGpcSessionId,
  setMessages,
  setAttachments,
  setTurns,
  setChatCards,
  clearPendingIntentChoiceSelection,
  setInFlight,
  setPendingRequestIdSynced,
  setInFlightUserMsg,
  setPendingClarification,
  setInFlightClarification,
  setPendingTopicClarification,
  setInFlightTopicFocus,
  ensureSession,
}: Inputs) {

  const handleRename = useCallback(
    async (id: string, title: string) => {
      try {
        await renameSession(id, title);
        await refreshSessions();
        notify('success', 'Renamed.');
      } catch (e: any) {
        notify('error', e?.message || 'Rename failed.');
      }
    },
    [notify, refreshSessions]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await deleteSession(id);
        setSessions((prev) => prev.filter((s) => s.session_id !== id));
        if (activeSessionId === id) {
          activeSessionIdRef.current = null;
          setActiveSessionId(null);
          setGpcChatView(null);
          setMrChatView(null);
          setConstructionSowChatView(null);
          setServicesPwsChatView(null);
          setRehydratingGpcSessionId(null);
          setMessages([]);
          setAttachments([]);
          setTurns([]);
          setChatCards([]);
          clearPendingIntentChoiceSelection();
          // Clear any in-flight signals that hydrated from the deleted
          // session's explicit pending request. Without this, the empty-state placeholder
          // would stay suppressed after the row is gone. FULL teardown via
          // the authoritative shared reset; `pendingRequestId` uses the
          // ref-syncing setter because deletion IS a navigation/staleness
          // signal.
          resetTransientTurnState({
            setInFlight,
            clearPendingRequestId: () => setPendingRequestIdSynced(null),
            setInFlightUserMsg,
            setPendingClarification,
            setInFlightClarification,
            setPendingTopicClarification,
            setInFlightTopicFocus,
          });
        }
        notify('success', 'Deleted.');
      } catch (e: any) {
        notify('error', e?.message || 'Delete failed.');
      }
    },
    [activeSessionId, notify, setPendingRequestIdSynced, clearPendingIntentChoiceSelection]
  );

  const handleAttach = useCallback(
    async (file: File) => {
      // Resolve to a real server-side session id. On the pending
      // placeholder (or with no active selection at all) this lazy-
      // creates a row server-side. `ensureSession` dedupes concurrent
      // calls so simultaneous send+drop only POSTs /sessions once.
      let targetId: string;
      try {
        targetId = await ensureSession();
      } catch (e: any) {
        notify('error', e?.message || 'Failed to start a new chat.');
        return;
      }
      try {
        await uploadAttachment(targetId, file);
        const fresh = await listAttachments(targetId);
        setAttachments(fresh);
      } catch (e: any) {
        notify('error', e?.message || 'Upload failed.');
      }
    },
    [ensureSession, notify]
  );

  const handleRemoveAttachment = useCallback(
    async (filename: string) => {
      if (!activeSessionId) return;
      try {
        await deleteAttachment(activeSessionId, filename);
        const fresh = await listAttachments(activeSessionId);
        setAttachments(fresh);
      } catch (e: any) {
        notify('error', e?.message || 'Remove failed.');
      }
    },
    [activeSessionId, notify]
  );

  return {
    handleRename,
    handleDelete,
    handleAttach,
    handleRemoveAttachment,
  };
}

export type HomeChatSessionActions = ReturnType<typeof useHomeChatSessionActions>;
